import classNames from "classnames/bind";

import styles from "./DiagramChart.module.css";
import { getEnd } from "../../utils/getEnd";
// @ts-ignore
import { getIsInRange } from "../../utils/getIsInRange";

const cx = classNames.bind(styles);

type SquareProps = {
  start: number;
  end: number;
  min?: number;
  max?: number;
  value?: number;
  fill: string;
};

type Props = {
  min?: number;
  max?: number;
  value?: number | [number, number];
  type?: "COMMON";
};

const WIDTH = 96;
const HEIGHT = 32;

const RECT_HEIGHT = 6;

const DiagramChartRect: React.FC<SquareProps> = ({ start, end, fill }) => {
  if (!end) {
    return null;
  }

  return (
    <rect
      x={(start / 100) * WIDTH}
      y={(HEIGHT - RECT_HEIGHT) / 2}
      height={RECT_HEIGHT}
      width={(end / 100) * WIDTH}
      fill={fill}
    />
  );
};

const sumValue = (items: number[]) => items.reduce((a, b) => a + b, 0);

export const DiagramChart: React.FC<Props> = ({ min, max, value, type }) => {
  const totalValue = value ? sumValue(([] as number[]).concat(value)) : 0;

  const isTotalInRange = getIsInRange(totalValue, min, max);

  const getColor = () => {
    if (type === "COMMON") {
      return "url(#commonGradient)";
    }

    return isTotalInRange ? "#5CC237" : "#E92D21";
  };

  const color = getColor();

  return (
    <div className={cx("container")}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className={cx("svg")}
        vectorEffect="non-scaling-size"
      >
        <path fill="none" stroke="var(--light)" d={`M 0.5,0 v ${HEIGHT}`} />
        <path fill="none" stroke="var(--light)" d={`M 31.5,0 v ${HEIGHT}`} />
        <path fill="none" stroke="var(--light)" d={`M 63.5,0 v ${HEIGHT}`} />
        <path fill="none" stroke="var(--light)" d={`M 95.5,0 v ${HEIGHT}`} />
        {Array.isArray(value) ? (
          value.map((current, index, list) => {
            const totalAtPrev = sumValue(list.slice(0, index));
            const totalAtCurrent = sumValue(list.slice(0, index + 1));
            const prevItemEnd =
              index !== 0 ? getEnd(totalAtPrev, min, max, totalValue) || 0 : 0;

            if (current === 0) {
              return null;
            }

            return (
              <DiagramChartRect
                key={index}
                fill={
                  [
                    isTotalInRange
                      ? "url(#greenGradient)"
                      : "url(#redGradient)",
                    color,
                  ][index]
                }
                start={prevItemEnd}
                end={getEnd(totalAtCurrent, min, max, totalValue) - prevItemEnd}
              />
            );
          })
        ) : (
          <DiagramChartRect
            fill={color}
            start={0}
            end={getEnd(value, min, max, totalValue)}
          />
        )}
      </svg>
    </div>
  );
};
